// Service-account auth for talking to the ERC ASMX upstream directly (QA).
//
// In dev/prod the React app talks to the BFF, which obtains the upstream
// "service token" via generate_token and injects it server-side. In QA the
// app points straight at the ASMX (REACT_APP_API_URL=…/ERC_MOB_V2_WS.asmx),
// so the browser must fetch that token itself and attach it to every request.
//
// Header contract is mirrored from server/proxyRoutes.js:
//   X-ACCESS-TOKEN: <serviceToken>            (always)
//   Authorization:  <user JWT> | Bearer <serviceToken>
//
// The token is kept in memory only (it is short-lived and re-fetched on
// startup / TTL / 401), so it never touches localStorage.

import axios from 'axios';
import { getItemSync } from '../utils/secureStorage';

const baseURL = process.env.REACT_APP_API_URL;

const config = {
  generateURL: `${baseURL}/generate_token`,
  username: process.env.REACT_APP_API_USERNAME,
  password: process.env.REACT_APP_API_PASSWORD,
  tokenTTLms: 60 * 60 * 1000,
  refreshSkewMs: 60 * 1000
};

// Dedicated instance with no interceptors so fetching the token never recurses
// through the auth interceptor we attach to the shared axios instance.
const authAxios = axios.create({ timeout: 15000 });

let tokenState = { token: null, expiresAt: 0 };
let refreshPromise = null;

function isFresh() {
  return (
    !!tokenState.token &&
    Date.now() + config.refreshSkewMs < tokenState.expiresAt
  );
}

function setToken(token) {
  tokenState = { token, expiresAt: Date.now() + config.tokenTTLms };
}

export function clearAuth() {
  tokenState = { token: null, expiresAt: 0 };
}

// Mirrors extractTokenFromResponseBody() in server/tokenService.js: ASMX may
// answer with JSON { d: { Success, Token } }, plaintext "true\r\n<token>", or
// a bare token string.
function extractToken(body) {
  if (body && typeof body === 'object') {
    const d = body.d ?? body;
    if (d && (d.Success === true || d.success === true)) {
      const t = d.Token ?? d.token;
      if (typeof t === 'string' && t.length > 0) return t;
    }
    return null;
  }
  if (typeof body === 'string') {
    try {
      const parsed = JSON.parse(body);
      const fromJson = extractToken(parsed);
      if (fromJson) return fromJson;
    } catch {
      // not JSON — fall through to plaintext handling
    }
    const lines = body
      .split(/\r?\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (lines.length >= 2 && /^true$/i.test(lines[0]) && lines[1].length >= 16) {
      return lines[1];
    }
    const m = body.match(/[A-Za-z0-9]{24,}/);
    if (m) return m[0];
  }
  return null;
}

async function fetchToken() {
  const res = await authAxios.post(
    config.generateURL,
    { username: config.username, password: config.password },
    { headers: { Accept: 'application/json, text/plain' } }
  );
  const token = extractToken(res.data);
  if (res.status === 200 && token) return token;
  throw new Error('generate_token: invalid response');
}

// Single-flight: concurrent callers share one in-progress request.
export function ensureToken() {
  if (isFresh()) return Promise.resolve(tokenState.token);
  if (!refreshPromise) {
    refreshPromise = fetchToken()
      .then((t) => {
        setToken(t);
        return t;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

function forceRefreshToken() {
  clearAuth();
  return ensureToken();
}

function isApiRequest(reqConfig, scoped) {
  if (scoped) return true;
  const fullUrl = `${reqConfig.baseURL || ''}${reqConfig.url || ''}`;
  return fullUrl.startsWith(baseURL);
}

function applyAuthHeaders(headers, serviceToken) {
  const userJwt = getItemSync('access_token');
  if (serviceToken) headers['X-ACCESS-TOKEN'] = serviceToken;
  if (userJwt) {
    headers.token = userJwt;
    headers.Authorization = userJwt;
  } else if (serviceToken) {
    headers.Authorization = `Bearer ${serviceToken}`;
  }
}

// Attach to a shared axios instance. `scoped: true` for instances whose baseURL
// is already the ERC API (every request targets it); otherwise we guard on the
// request URL so foreign hosts (survey portal, Facebook) are left untouched.
export function attachAuthInterceptors(instance, { scoped = false } = {}) {
  instance.interceptors.request.use(async (req) => {
    if (!isApiRequest(req, scoped)) return req;
    const serviceToken = await ensureToken().catch(() => null);
    req.headers = req.headers || {};
    applyAuthHeaders(req.headers, serviceToken);
    return req;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (error) => {
      const { response, config: reqConfig } = error || {};
      if (
        !response ||
        !reqConfig ||
        response.status !== 401 ||
        reqConfig._retry ||
        !isApiRequest(reqConfig, scoped)
      ) {
        return Promise.reject(error);
      }
      reqConfig._retry = true;
      const newToken = await forceRefreshToken().catch(() => null);
      if (!newToken) return Promise.reject(error);
      reqConfig.headers = reqConfig.headers || {};
      applyAuthHeaders(reqConfig.headers, newToken);
      return instance.request(reqConfig);
    }
  );
}
